import { DocumentData, QueryConstraint, count, doc, getAggregateFromServer, getDoc, getDocs, limit, query, sum, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { ADMIN_ANALYTICS_COLLECTION, ADMIN_ANALYTICS_DOCS, COLLECTIONS } from "./collectionNames";
import { adminCollection, logDevelopmentError, stringArrayField, stringField } from "./firestoreHelpers";

export type FounderGrowthStatus = "growing" | "healthy" | "watching" | "future" | "unavailable";

export type FounderGrowthMetricId =
  | "newUsersToday"
  | "newCoaches"
  | "newStudios"
  | "newOrganizations"
  | "newBookingAgents"
  | "bookingsToday"
  | "challengesCreated"
  | "servicesPublished"
  | "opportunitiesPublished"
  | "revenueToday"
  | "revenueThisMonth"
  | "mrr"
  | "growthRate"
  | "retention"
  | "returningUsers";

export type FounderGrowthMetric = {
  id: FounderGrowthMetricId;
  title: string;
  value: string;
  story: string;
  detail: string;
  source: string;
  status: FounderGrowthStatus;
  unavailableReason: string | null;
};

export type FounderGrowthStory = {
  id: string;
  icon: string;
  headline: string;
  insight: string;
  status: FounderGrowthStatus;
};

export type FounderGrowthDashboard = {
  hero: FounderGrowthStory;
  stories: FounderGrowthStory[];
  metrics: FounderGrowthMetric[];
  lastUpdated: Date;
  error: string | null;
};

type UserRoleCounts = {
  total: number | null;
  coaches: number | null;
  studios: number | null;
  organizations: number | null;
  bookingAgents: number | null;
  unavailableReason: string | null;
};

const PAID_REVENUE_STATUS = "paid";
const USER_SAMPLE_LIMIT = 500;

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfLocalMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatCount(value: number | null) {
  return value === null ? "Unavailable" : new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function formatUSD(value: number | null) {
  return value === null
    ? "Unavailable"
    : new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
}

function formatPercentValue(value: number | null) {
  return value === null ? "Unavailable" : new Intl.NumberFormat(undefined, { style: "percent", maximumFractionDigits: 0 }).format(value);
}

function percentageChange(current: number | null, previous: number | null) {
  if (current === null || previous === null) {
    return null;
  }

  if (previous === 0) {
    return current > 0 ? 1 : 0;
  }

  return (current - previous) / previous;
}

function normalizePercent(value: unknown) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }

  return value > 1 ? value / 100 : value;
}

function metric(
  id: FounderGrowthMetricId,
  title: string,
  value: string,
  story: string,
  detail: string,
  source: string,
  status: FounderGrowthStatus,
  unavailableReason: string | null = null
): FounderGrowthMetric {
  return {
    id,
    title,
    value,
    story,
    detail,
    source,
    status,
    unavailableReason
  };
}

function unavailableMetric(id: FounderGrowthMetricId, title: string, source: string, reason: string, story: string) {
  return metric(id, title, "Unavailable", story, reason, source, "unavailable", reason);
}

async function safeCount(collectionName: string, constraints: QueryConstraint[] = []) {
  try {
    const snapshot = await getAggregateFromServer(query(adminCollection(collectionName), ...constraints), {
      total: count()
    });

    return { value: snapshot.data().total ?? 0, unavailableReason: null };
  } catch (error) {
    logDevelopmentError(error);
    return { value: null, unavailableReason: "The aggregate query could not be read." };
  }
}

async function safePlatformRevenue(start: Date, end: Date) {
  try {
    const snapshot = await getAggregateFromServer(
      query(
        adminCollection(COLLECTIONS.marketplaceTransactions),
        where("status", "==", PAID_REVENUE_STATUS),
        where("createdAt", ">=", start),
        where("createdAt", "<", end)
      ),
      { total: sum("platformFeeUSD") }
    );

    return { value: snapshot.data().total ?? 0, unavailableReason: null };
  } catch (error) {
    logDevelopmentError(error);
    return { value: null, unavailableReason: "The paid marketplace revenue aggregate could not be read." };
  }
}

function userRoles(data: DocumentData) {
  const roles = new Set<string>();
  const stringRoleFields = ["role", "activeRole", "primaryRole", "accountType", "verificationType"];
  const arrayRoleFields = ["roles", "assignedRoles", "completedRoleTargets"];

  stringRoleFields.forEach((field) => {
    const value = stringField(data, field);
    if (value) {
      roles.add(value.toLowerCase());
    }
  });

  arrayRoleFields.forEach((field) => {
    stringArrayField(data, field).forEach((value) => roles.add(value.toLowerCase()));
  });

  if (data.isCoach === true) roles.add("coach");
  if (data.isStudio === true || data.isStudioOwner === true) roles.add("studio");
  if (data.isOrganization === true) roles.add("organization");
  if (data.isBookingAgent === true || data.isAgent === true) roles.add("bookingagent");

  return roles;
}

function hasAnyRole(roles: Set<string>, acceptedRoles: string[]) {
  return acceptedRoles.some((role) => roles.has(role.toLowerCase()));
}

async function usersCreatedBetween(start: Date, end: Date): Promise<UserRoleCounts> {
  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.users), where("createdAt", ">=", start), where("createdAt", "<", end), limit(USER_SAMPLE_LIMIT))
    );

    let coaches = 0;
    let studios = 0;
    let organizations = 0;
    let bookingAgents = 0;

    snapshot.docs.forEach((document) => {
      const roles = userRoles(document.data());
      if (hasAnyRole(roles, ["coach"])) coaches += 1;
      if (hasAnyRole(roles, ["studio", "studioowner", "studio_owner"])) studios += 1;
      if (hasAnyRole(roles, ["organization"])) organizations += 1;
      if (hasAnyRole(roles, ["bookingagent", "booking_agent", "agent"])) bookingAgents += 1;
    });

    return {
      total: snapshot.size,
      coaches,
      studios,
      organizations,
      bookingAgents,
      unavailableReason: null
    };
  } catch (error) {
    logDevelopmentError(error);
    return {
      total: null,
      coaches: null,
      studios: null,
      organizations: null,
      bookingAgents: null,
      unavailableReason: "Recent user records could not be read."
    };
  }
}

function numericDocumentValue(data: DocumentData, keys: string[]) {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
}

async function retentionSignals() {
  try {
    const snapshot = await getDoc(doc(db, ADMIN_ANALYTICS_COLLECTION, ADMIN_ANALYTICS_DOCS.retention));

    if (!snapshot.exists()) {
      return {
        retention: null,
        returningUsers: null,
        source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.retention}`,
        unavailableReason: "Retention aggregate is not configured yet."
      };
    }

    const data = snapshot.data();
    return {
      retention: normalizePercent(numericDocumentValue(data, ["retentionRate", "retention", "rate", "value"])),
      returningUsers: numericDocumentValue(data, ["returningUsersToday", "returningUsers", "returnedToday"]),
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.retention}`,
      unavailableReason: null
    };
  } catch (error) {
    logDevelopmentError(error);
    return {
      retention: null,
      returningUsers: null,
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.retention}`,
      unavailableReason: "Retention aggregate could not be read."
    };
  }
}

function sourceFor(collectionName: string, rangeLabel = "createdAt") {
  return `${collectionName}.${rangeLabel}`;
}

function storyForDelta(subject: string, delta: number | null, period: string) {
  if (delta === null) {
    return `${subject} growth will appear when comparison data is available.`;
  }

  if (delta > 0) {
    return `${subject} grew ${formatPercentValue(delta)} ${period}.`;
  }

  if (delta < 0) {
    return `${subject} slowed ${formatPercentValue(Math.abs(delta))} ${period}.`;
  }

  return `${subject} held steady ${period}.`;
}

function metricStatusForCount(value: number | null): FounderGrowthStatus {
  if (value === null) return "unavailable";
  return value > 0 ? "growing" : "watching";
}

function heroStory(newUsersToday: number | null, weeklyGrowth: number | null, returningUsers: number | null): FounderGrowthStory {
  if (weeklyGrowth !== null) {
    return {
      id: "weekly-growth",
      icon: "📈",
      headline: weeklyGrowth >= 0 ? "Chor is growing." : "Growth needs attention.",
      insight: storyForDelta("New users", weeklyGrowth, "this week"),
      status: weeklyGrowth >= 0 ? "growing" : "watching"
    };
  }

  if (returningUsers !== null) {
    return {
      id: "returning-users",
      icon: "❤️",
      headline: `${formatCount(returningUsers)} people returned today.`,
      insight: "Returning users are the clearest signal that Chor is becoming a habit.",
      status: returningUsers > 0 ? "healthy" : "watching"
    };
  }

  return {
    id: "new-users",
    icon: "🚀",
    headline: `${formatCount(newUsersToday)} new users today.`,
    insight: "New user activity is the first signal of Chor's daily growth.",
    status: metricStatusForCount(newUsersToday)
  };
}

export async function fetchFounderGrowthDashboard(now = new Date()): Promise<FounderGrowthDashboard> {
  const todayStart = startOfLocalDay(now);
  const tomorrowStart = addDays(todayStart, 1);
  const yesterdayStart = addDays(todayStart, -1);
  const weekStart = addDays(todayStart, -7);
  const previousWeekStart = addDays(todayStart, -14);
  const monthStart = startOfLocalMonth(now);

  const [
    todaysUsers,
    yesterdaysUsers,
    currentWeekUsers,
    previousWeekUsers,
    bookingsToday,
    challengesCreated,
    servicesPublished,
    opportunitiesPublished,
    revenueToday,
    revenueThisMonth,
    retention
  ] = await Promise.all([
    usersCreatedBetween(todayStart, tomorrowStart),
    usersCreatedBetween(yesterdayStart, todayStart),
    safeCount(COLLECTIONS.users, [where("createdAt", ">=", weekStart), where("createdAt", "<", tomorrowStart)]),
    safeCount(COLLECTIONS.users, [where("createdAt", ">=", previousWeekStart), where("createdAt", "<", weekStart)]),
    safeCount(COLLECTIONS.bookings, [where("createdAt", ">=", todayStart), where("createdAt", "<", tomorrowStart)]),
    safeCount(COLLECTIONS.challenges, [where("createdAt", ">=", todayStart), where("createdAt", "<", tomorrowStart)]),
    safeCount(COLLECTIONS.classSessions, [where("createdAt", ">=", todayStart), where("createdAt", "<", tomorrowStart)]),
    safeCount(COLLECTIONS.opportunities, [where("createdAt", ">=", todayStart), where("createdAt", "<", tomorrowStart)]),
    safePlatformRevenue(todayStart, tomorrowStart),
    safePlatformRevenue(monthStart, tomorrowStart),
    retentionSignals()
  ]);

  const dailyUserGrowth = percentageChange(todaysUsers.total, yesterdaysUsers.total);
  const weeklyGrowth = percentageChange(currentWeekUsers.value, previousWeekUsers.value);
  const roleSource = sourceFor(COLLECTIONS.users, "createdAt + role fields");
  const roleUnavailable = todaysUsers.unavailableReason;

  const metrics: FounderGrowthMetric[] = [
    metric(
      "newUsersToday",
      "New Users Today",
      formatCount(todaysUsers.total),
      todaysUsers.total === 1 ? "One new person joined Chor today." : `${formatCount(todaysUsers.total)} people joined Chor today.`,
      "Daily signups derive from users created today.",
      sourceFor(COLLECTIONS.users),
      metricStatusForCount(todaysUsers.total),
      todaysUsers.unavailableReason
    ),
    roleUnavailable
      ? unavailableMetric("newCoaches", "New Coaches", roleSource, roleUnavailable, "Coach growth will appear when user role truth loads.")
      : metric("newCoaches", "New Coaches", formatCount(todaysUsers.coaches), `${formatCount(todaysUsers.coaches)} coaches joined today.`, "Counts users created today with coach role signals.", roleSource, metricStatusForCount(todaysUsers.coaches)),
    roleUnavailable
      ? unavailableMetric("newStudios", "New Studios", roleSource, roleUnavailable, "Studio growth will appear when user role truth loads.")
      : metric("newStudios", "New Studios", formatCount(todaysUsers.studios), `${formatCount(todaysUsers.studios)} studios joined today.`, "Counts users created today with studio role signals.", roleSource, metricStatusForCount(todaysUsers.studios)),
    roleUnavailable
      ? unavailableMetric("newOrganizations", "New Organizations", roleSource, roleUnavailable, "Organization growth will appear when user role truth loads.")
      : metric("newOrganizations", "New Organizations", formatCount(todaysUsers.organizations), `${formatCount(todaysUsers.organizations)} organizations joined today.`, "Counts users created today with organization role signals.", roleSource, metricStatusForCount(todaysUsers.organizations)),
    roleUnavailable
      ? unavailableMetric("newBookingAgents", "New Booking Agents", roleSource, roleUnavailable, "Booking Agent growth will appear when user role truth loads.")
      : metric("newBookingAgents", "New Booking Agents", formatCount(todaysUsers.bookingAgents), `${formatCount(todaysUsers.bookingAgents)} booking agents joined today.`, "Counts users created today with booking agent role signals.", roleSource, metricStatusForCount(todaysUsers.bookingAgents)),
    metric("bookingsToday", "Bookings Today", formatCount(bookingsToday.value), `${formatCount(bookingsToday.value)} bookings happened today.`, "Bookings created today from canonical booking records.", sourceFor(COLLECTIONS.bookings), metricStatusForCount(bookingsToday.value), bookingsToday.unavailableReason),
    metric("challengesCreated", "Challenges Created", formatCount(challengesCreated.value), `${formatCount(challengesCreated.value)} challenges were created today.`, "Challenge creation from challenge records.", sourceFor(COLLECTIONS.challenges), metricStatusForCount(challengesCreated.value), challengesCreated.unavailableReason),
    metric("servicesPublished", "Services Published", formatCount(servicesPublished.value), `${formatCount(servicesPublished.value)} services were published today.`, "Service publishing derives from classSessions created today.", sourceFor(COLLECTIONS.classSessions), metricStatusForCount(servicesPublished.value), servicesPublished.unavailableReason),
    metric("opportunitiesPublished", "Opportunities Published", formatCount(opportunitiesPublished.value), `${formatCount(opportunitiesPublished.value)} opportunities were published today.`, "Opportunity publishing derives from opportunities created today.", sourceFor(COLLECTIONS.opportunities), metricStatusForCount(opportunitiesPublished.value), opportunitiesPublished.unavailableReason),
    metric("revenueToday", "Revenue Today", formatUSD(revenueToday.value), `${formatUSD(revenueToday.value)} in platform revenue today.`, "Paid marketplace platform fees collected today.", sourceFor(COLLECTIONS.marketplaceTransactions, "paid createdAt + platformFeeUSD"), revenueToday.value && revenueToday.value > 0 ? "healthy" : metricStatusForCount(revenueToday.value), revenueToday.unavailableReason),
    metric("revenueThisMonth", "Revenue This Month", formatUSD(revenueThisMonth.value), `${formatUSD(revenueThisMonth.value)} in platform revenue this month.`, "Paid marketplace platform fees collected this month.", sourceFor(COLLECTIONS.marketplaceTransactions, "paid createdAt + platformFeeUSD"), revenueThisMonth.value && revenueThisMonth.value > 0 ? "healthy" : metricStatusForCount(revenueThisMonth.value), revenueThisMonth.unavailableReason),
    metric("mrr", "MRR", "Future", "Subscription revenue is not active yet.", "MRR remains a future subscription signal until canonical subscription revenue exists.", COLLECTIONS.subscriptions, "future"),
    metric("growthRate", "Growth Rate", formatPercentValue(dailyUserGrowth), storyForDelta("New users", dailyUserGrowth, "versus yesterday"), "Compares new users today with new users yesterday.", sourceFor(COLLECTIONS.users, "createdAt comparison"), dailyUserGrowth === null ? "unavailable" : dailyUserGrowth >= 0 ? "growing" : "watching", todaysUsers.unavailableReason ?? yesterdaysUsers.unavailableReason),
    retention.retention === null
      ? unavailableMetric("retention", "Retention", retention.source, retention.unavailableReason ?? "Retention aggregate is unavailable.", "Retention will appear when the retention aggregate is configured.")
      : metric("retention", "Retention", formatPercentValue(retention.retention), `${formatPercentValue(retention.retention)} of users returned in the current retention window.`, "Retention derives from the canonical admin retention aggregate.", retention.source, retention.retention > 0 ? "healthy" : "watching"),
    retention.returningUsers === null
      ? unavailableMetric("returningUsers", "Returning Users", retention.source, retention.unavailableReason ?? "Returning-user aggregate is unavailable.", "Returning-user stories will appear when retention truth is configured.")
      : metric("returningUsers", "Returning Users", formatCount(retention.returningUsers), `${formatCount(retention.returningUsers)} users returned today.`, "Returning users derive from the canonical admin retention aggregate.", retention.source, retention.returningUsers > 0 ? "healthy" : "watching")
  ];

  const stories: FounderGrowthStory[] = [
    {
      id: "coach-growth",
      icon: "📈",
      headline: `${formatCount(todaysUsers.coaches)} new coaches today.`,
      insight: todaysUsers.coaches && todaysUsers.coaches > 0 ? "More teachers are joining the Chor marketplace." : "Coach growth is quiet today.",
      status: metricStatusForCount(todaysUsers.coaches)
    },
    {
      id: "booking-energy",
      icon: "🎟",
      headline: `${formatCount(bookingsToday.value)} bookings today.`,
      insight: bookingsToday.value && bookingsToday.value > 0 ? "Dancers are turning discovery into committed sessions." : "Booking activity has not started today.",
      status: metricStatusForCount(bookingsToday.value)
    },
    {
      id: "returning-community",
      icon: "❤️",
      headline: retention.returningUsers === null ? "Returning-user signal pending." : `${formatCount(retention.returningUsers)} users returned today.`,
      insight: retention.returningUsers === null ? "Retention aggregation will make habit growth visible here." : "Returning users show Chor is becoming part of people's routine.",
      status: retention.returningUsers === null ? "unavailable" : retention.returningUsers > 0 ? "healthy" : "watching"
    }
  ];

  return {
    hero: heroStory(todaysUsers.total, weeklyGrowth, retention.returningUsers),
    stories,
    metrics,
    lastUpdated: new Date(),
    error: null
  };
}
